import React, { useCallback, useEffect, useState } from 'react';
import './style.less';

const STORAGE_KEY = 'highdim_cookie_consent';

const DEFAULT_PREFERENCES = {
  essential: true,
  analytics: false,
  functional: false,
  marketing: false,
};

const COOKIE_CATEGORIES = [
  {
    key: 'essential',
    title: 'Essential Cookies',
    description:
      'Required for the website to function properly. These cannot be disabled.',
    required: true,
  },
  {
    key: 'analytics',
    title: 'Analytics Cookies',
    description:
      'Help us understand how visitors interact with our website so we can improve performance.',
  },
  {
    key: 'functional',
    title: 'Functional Cookies',
    description:
      'Enable enhanced features and personalization, such as remembering your preferences.',
  },
  {
    key: 'marketing',
    title: 'Marketing Cookies',
    description:
      'Used to deliver relevant advertisements and measure campaign effectiveness.',
  },
];

const readStoredConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed.preferences,
      essential: true,
      timestamp: parsed.timestamp,
    };
  } catch {
    return null;
  }
};

const persistConsent = (preferences) => {
  const payload = {
    preferences: { ...preferences, essential: true },
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: payload }));
  return payload;
};

const CookieConsent = ({ privacyPolicyUrl = '/contact' }) => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setPreferences(stored);
    } else {
      setVisible(true);
    }
  }, []);

  const closeBanner = useCallback((nextPreferences) => {
    persistConsent(nextPreferences);
    setPreferences(nextPreferences);
    setVisible(false);
    setShowSettings(false);
  }, []);

  const handleAcceptAll = () => {
    closeBanner({
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    });
  };

  const handleRejectNonEssential = () => {
    closeBanner({ ...DEFAULT_PREFERENCES });
  };

  const handleSavePreferences = () => {
    closeBanner(preferences);
  };

  const handleToggle = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!visible) return null;

  return (
    <>
      {visible && (
        <div className="cookie-consent" role="dialog" aria-labelledby="cookie-consent-title" aria-modal="true">
          <div className="cookie-consent__panel">
            {!showSettings ? (
              <div className="cookie-consent__banner">
                <div className="cookie-consent__content">
                  <h2 id="cookie-consent-title" className="cookie-consent__title">
                    Your Privacy Matters
                  </h2>
                  <p className="cookie-consent__text">
                    We use cookies and similar technologies to operate our website, analyze
                    traffic, and improve your experience. In accordance with Canada&apos;s{' '}
                    <strong>PIPEDA</strong> and applicable provincial privacy laws, we ask for
                    your consent before placing non-essential cookies on your device.
                  </p>
                  <p className="cookie-consent__text cookie-consent__text--secondary">
                    You may accept all cookies, reject non-essential cookies, or customize your
                    preferences. Read our{' '}
                    <a href={privacyPolicyUrl} className="cookie-consent__link">
                      Privacy Policy
                    </a>{' '}
                    for more details.
                  </p>
                </div>
                <div className="cookie-consent__actions">
                  <button
                    type="button"
                    className="cookie-consent__btn cookie-consent__btn--primary"
                    onClick={handleAcceptAll}
                  >
                    Accept All
                  </button>
                  <button
                    type="button"
                    className="cookie-consent__btn cookie-consent__btn--secondary"
                    onClick={handleRejectNonEssential}
                  >
                    Reject Non-Essential
                  </button>
                  <button
                    type="button"
                    className="cookie-consent__btn cookie-consent__btn--ghost"
                    onClick={() => setShowSettings(true)}
                  >
                    Cookie Preferences
                  </button>
                </div>
              </div>
            ) : (
              <div className="cookie-consent__settings">
                <h2 className="cookie-consent__title">Cookie Preferences</h2>
                <p className="cookie-consent__text cookie-consent__text--secondary">
                  Manage how we use cookies on this website. Essential cookies are always
                  active. You can change your choices at any time.
                </p>
                <ul className="cookie-consent__categories">
                  {COOKIE_CATEGORIES.map((category) => (
                    <li key={category.key} className="cookie-consent__category">
                      <div className="cookie-consent__category-info">
                        <span className="cookie-consent__category-title">{category.title}</span>
                        <p className="cookie-consent__category-desc">{category.description}</p>
                      </div>
                      {category.required ? (
                        <span className="cookie-consent__badge">Always Active</span>
                      ) : (
                        <label className="cookie-consent__switch">
                          <input
                            type="checkbox"
                            checked={preferences[category.key]}
                            onChange={() => handleToggle(category.key)}
                          />
                          <span className="cookie-consent__switch-slider" />
                        </label>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="cookie-consent__actions cookie-consent__actions--settings">
                  <button
                    type="button"
                    className="cookie-consent__btn cookie-consent__btn--primary"
                    onClick={handleSavePreferences}
                  >
                    Save Preferences
                  </button>
                  <button
                    type="button"
                    className="cookie-consent__btn cookie-consent__btn--ghost"
                    onClick={() => setShowSettings(false)}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
